import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { MailIcon } from 'lucide-react';
import { SUPPORT_EMAIL } from '../data/launch';

/**
 * INTERNAL NOTE — not rendered.
 * The in-app navigation path below is CONFIRMED against the production app:
 * Home → Settings → Support → Delete Account.
 * TO CONFIRM before this page is relied on for a store review:
 *  - the exact backend deletion behaviour, field by field: what is deleted,
 *    what is anonymised, what is retained, and how long it takes
 * The wording below is deliberately non-absolute because that behaviour has not
 * been verified. Tighten it once confirmed. See
 * docs/internal-pre-launch-checklist.md section 1, item 8.
 */
const steps = [{
  title: 'Open KittyPot and sign in.'
}, {
  title: 'From Home, go to Settings → Support → Delete Account.'
}, {
  title: 'Confirm.',
  detail: 'We remove or anonymise your account information where appropriate. Limited shared records may need to remain so other members’ shared expense history and balances stay accurate.'
}];
export function DeleteAccount() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-[#FAFAFA] font-sans selection:bg-kitty-yellow-200 selection:text-kitty-dark" data-id="element-478" data-mp="qm6mzxgd3fev">
      <SEO title="Delete your KittyPot account | KittyPot" description="How to permanently delete your KittyPot account from the app, and what happens to your data when you do." ogTitle="Delete your KittyPot account" ogDescription="How to permanently delete your KittyPot account and what happens to your data." path="/delete-account" data-id="element-479" data-mp="1ifgrsy1t2g5h" />
      <Navbar data-id="element-480" data-mp="3p7zqzpd30jd6" />
      <main className="pt-36 pb-24" data-id="element-481" data-mp="3u4zvhvua9e26">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-482" data-mp="3u617q3n1c6lh">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-483" data-mp="3a2viy6zn0fdk">
            <h1 className="text-4xl md:text-5xl font-black text-kitty-dark mb-5 tracking-tight" data-id="element-484" data-mp="3623ahfylaeic">
              Delete your KittyPot account
            </h1>
            <p className="text-lg text-gray-500 mb-8 max-w-xl" data-id="element-485" data-mp="2wca02ia6o5fo">
              You can delete your KittyPot account at any time, from inside the
              app or by asking us. There are two routes below.
            </p>

            <div className="mb-12 rounded-2xl border border-gray-200 bg-white p-6 md:p-7" data-id="element-486" data-mp="2mcj74l19tyhp">
              <h2 className="text-lg font-bold text-kitty-dark mb-3" data-id="element-487" data-mp="2icqvfzgkzc84">
                What deleting your account means
              </h2>
              <ul className="list-disc pl-5 space-y-2 text-gray-600 leading-relaxed" data-id="element-488" data-mp="2dep7b04asmps">
                <li data-id="element-489" data-mp="gjkqwf2f3kdk">
                  You will no longer be able to sign in, and your profile and
                  sign-in details are removed or anonymised where appropriate.
                </li>
                <li data-id="element-490" data-mp="j4ij1ciau6ad">
                  Anything you chose to save for settling up, such as bank
                  details, and any push notification registration are removed.
                </li>
                <li data-id="element-491" data-mp="svdf18lckk90">
                  Limited shared records may need to remain in kitties you took
                  part in, so other members' shared expense history and balances
                  stay accurate. Where records remain, your personal details are
                  removed or anonymised.
                </li>
                <li data-id="element-492" data-mp="56u56omnfzet">
                  We may keep a limited amount of information where we have a
                  legal reason to, for example to meet a legal obligation or to
                  deal with a dispute.
                </li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4" data-id="element-493" data-mp="1lihdsvtuk2cc">
                Deleting your account cannot be undone. If you want a copy of
                your data first, email us before you delete it.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-kitty-dark mb-5" data-id="element-494" data-mp="1j4lyoil1bthf">
              Delete it in the app
            </h2>
          </motion.div>

          <motion.ol initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="space-y-4 mb-12" data-id="element-495" data-mp="1y2wf4ko6vk9v">
            {steps.map((step, index) => <li key={step.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-7 flex gap-5" data-id="element-496" data-mp="223gdfrki0txl" data-mpi={index}>
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-kitty-yellow text-kitty-dark font-bold flex items-center justify-center" data-id="element-497" data-mp="2gufjauty94dh">
                  {index + 1}
                </span>
                <div data-id="element-498" data-mp="2ptat0gw7cwlj">
                  <p className="text-lg font-bold text-kitty-dark leading-snug" data-id="element-499" data-mp="2lrfqxa6g6f6p">
                    {step.title}
                  </p>
                  {step.detail && <p className="text-gray-500 leading-relaxed mt-2" data-id="element-500" data-mp="34gj99w4dbs4d">
                      {step.detail}
                    </p>}
                </div>
              </li>)}
          </motion.ol>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="bg-gray-950 rounded-2xl p-8 md:p-10 text-white" data-id="element-501" data-mp="30e4lqypr846r">
            <div className="w-11 h-11 rounded-2xl bg-white/[0.08] flex items-center justify-center mb-5" data-id="element-502" data-mp="2qgqki6cejlz1">
              <MailIcon className="w-5 h-5 text-kitty-yellow" aria-hidden="true" data-id="element-503" data-mp="2cq4yi8rnivwx" />
            </div>
            <h2 className="text-xl font-bold mb-3" data-id="element-504" data-mp="22qe5kbiqooyy">
              Or ask us to delete it
            </h2>
            <p className="text-gray-400 leading-relaxed" data-id="element-505" data-mp="27p7gbw6n61vf">
              If you cannot open the app, email{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-kitty-yellow underline underline-offset-2 transition-colors hover:text-kitty-yellow-500" data-id="element-506" data-mp="d9s73so6jw7n">
                {SUPPORT_EMAIL}
              </a>{' '}
              from the email on your KittyPot account and ask us to delete it.
              We will check it is you first, then confirm by email once it is
              done.
            </p>
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="text-sm text-gray-500 mt-8" data-id="element-507" data-mp="s6strhril0z9">
            For more on how we handle your data, read our{' '}
            <Link to="/privacy-policy" className="font-semibold text-kitty-dark underline underline-offset-2 transition-colors hover:text-kitty-yellow-700" data-id="element-508" data-mp="iappo6d3qs9o">
              Privacy Policy
            </Link>
            .
          </motion.p>
        </div>
      </main>
      <Footer data-id="element-509" data-mp="2sst2ojmzub72" />
    </div>;
}